/**
 * Application error codes. The database raises them as the error message with
 * SQLSTATE PT<status>; Edge Functions return them as `{ error: { code, message } }`.
 * The UI shows `ERROR_MESSAGES[code]` — never raw database text.
 */
export const ERROR_CODES = [
  'NOT_AUTHENTICATED',
  'PROFILE_NOT_FOUND',
  'VALIDATION_FAILED',
  'INVALID_TIMEZONE',
  'INVALID_INSTRUMENT',
  'ACTIVE_INSTRUMENT',
  'NO_INSTRUMENT',
  'NOT_FOUND',
  'LIMIT_REACHED',
  'CLASSROOM_NOT_FOUND',
  'CLASSROOM_ARCHIVED',
  'MEMBER_NOT_FOUND',
  'NOT_A_MEMBER',
  'INVALID_CODE',
  'ALREADY_MEMBER',
  'OWN_CLASSROOM',
  'REMOVED_FROM_CLASS',
  'RATE_LIMITED',
  'PLAN_NOT_FOUND',
  'PLAN_ARCHIVED',
  'PLAN_CHANGED',
  'PLAN_EMPTY',
  'ACTIVITY_NOT_FOUND',
  'TEMPLATE_NOT_FOUND',
  'UNSUPPORTED_EXERCISE',
  'INVALID_EXERCISE_CONFIG',
  'ASSIGNMENT_NOT_FOUND',
  'ASSIGNMENT_NOT_ACTIVE',
  'ASSIGNMENT_NOT_RELEASED',
  'ASSIGNMENT_REPLACED',
  'ASSIGNMENT_ARCHIVED',
  'LESSON_NOT_FOUND',
  'LESSON_LOCKED',
  'NOTHING_DUE',
  'SESSION_NOT_FOUND',
  'SESSION_NOT_ACTIVE',
  'SESSION_EXPIRED',
  'SESSION_INCOMPLETE',
  'QUESTION_NOT_IN_SESSION',
  'QUESTION_ALREADY_ANSWERED',
  'TOO_MANY_ATTEMPTS',
  'INVALID_ANSWER',
  'HINTS_DISABLED',
  'EVENT_CONFLICT',
  'IMMUTABLE_RECORD',
  'NETWORK_ERROR',
  'BACKEND_UNAVAILABLE',
  'UNKNOWN',
] as const;

export type AppErrorCode = (typeof ERROR_CODES)[number];

export const ERROR_MESSAGES: Record<AppErrorCode, string> = {
  NOT_AUTHENTICATED: 'Your session has ended. Sign in again to continue.',
  PROFILE_NOT_FOUND: 'We could not load your profile. Try signing in again.',
  VALIDATION_FAILED: 'Some details need attention. Check the form and try again.',
  INVALID_TIMEZONE: 'Choose a valid time zone.',
  INVALID_INSTRUMENT: 'Check the instrument’s clef and range (within three ledger lines).',
  ACTIVE_INSTRUMENT: 'Switch to another instrument before removing this one.',
  NO_INSTRUMENT: 'Choose an instrument in Settings first.',
  NOT_FOUND: 'That item could not be found.',
  LIMIT_REACHED: 'You have reached the limit. Archive something first.',
  CLASSROOM_NOT_FOUND: 'Classroom not found.',
  CLASSROOM_ARCHIVED: 'This classroom is archived.',
  MEMBER_NOT_FOUND: 'That student is not in this classroom.',
  NOT_A_MEMBER: 'You are no longer a member of that classroom.',
  INVALID_CODE: 'That code didn’t work. Check it with your teacher — codes can be changed or expire.',
  ALREADY_MEMBER: 'You are already in this classroom.',
  OWN_CLASSROOM: 'This is your own classroom — you can preview activities from the teacher workspace.',
  REMOVED_FROM_CLASS: 'You were removed from this classroom. Ask your teacher to add you back.',
  RATE_LIMITED: 'Too many attempts. Wait a few minutes and try again.',
  PLAN_NOT_FOUND: 'Plan not found.',
  PLAN_ARCHIVED: 'This plan is archived.',
  PLAN_CHANGED: 'The plan changed while publishing. Review it and publish again.',
  PLAN_EMPTY: 'Add at least one activity before publishing.',
  ACTIVITY_NOT_FOUND: 'Activity not found.',
  TEMPLATE_NOT_FOUND: 'Template not found.',
  UNSUPPORTED_EXERCISE: 'That exercise type is not available yet.',
  INVALID_EXERCISE_CONFIG: 'An activity’s settings are not valid. Fix the highlighted fields.',
  ASSIGNMENT_NOT_FOUND: 'Assignment not found.',
  ASSIGNMENT_NOT_ACTIVE: 'This assignment is no longer current.',
  ASSIGNMENT_NOT_RELEASED: 'This assignment is not open yet.',
  ASSIGNMENT_REPLACED: 'Your teacher updated this assignment. Open the new version from your home screen.',
  ASSIGNMENT_ARCHIVED: 'This assignment was removed by your teacher.',
  LESSON_NOT_FOUND: 'Lesson not found.',
  LESSON_LOCKED: 'Finish the earlier lessons first.',
  NOTHING_DUE: 'Nothing is due for review right now.',
  SESSION_NOT_FOUND: 'Practice session not found.',
  SESSION_NOT_ACTIVE: 'This practice session has already ended.',
  SESSION_EXPIRED: 'This practice session expired. Start a new one.',
  SESSION_INCOMPLETE: 'Answer every question before finishing.',
  QUESTION_NOT_IN_SESSION: 'That question is not part of this session.',
  QUESTION_ALREADY_ANSWERED: 'That question was already answered.',
  TOO_MANY_ATTEMPTS: 'Too many attempts on this question.',
  INVALID_ANSWER: 'That answer could not be read.',
  HINTS_DISABLED: 'Hints are turned off for this activity.',
  EVENT_CONFLICT: 'Something went wrong saving your answer. Please retry.',
  IMMUTABLE_RECORD: 'Published records cannot be changed.',
  NETWORK_ERROR: 'You appear to be offline. We’ll keep trying.',
  BACKEND_UNAVAILABLE: 'The service is unavailable right now. Try again shortly.',
  UNKNOWN: 'Something went wrong. Please try again.',
};

export function isAppErrorCode(value: unknown): value is AppErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
}

export class AppError extends Error {
  readonly code: AppErrorCode;
  readonly status: number;
  /** Safe, human-readable detail from the server (never includes SQL). */
  readonly detail: string | undefined;

  constructor(code: AppErrorCode, status = 400, detail?: string) {
    super(ERROR_MESSAGES[code]);
    this.name = 'AppError';
    this.code = code;
    this.status = status;
    this.detail = detail;
  }

  get userMessage(): string {
    return this.detail && this.code === 'VALIDATION_FAILED' ? this.detail : ERROR_MESSAGES[this.code];
  }
}

/**
 * Normalize an error from PostgREST / supabase-js / fetch into an AppError.
 * PostgREST errors look like { code: 'PT404', message: 'CLASSROOM_NOT_FOUND', details: '…' }.
 */
export function toAppError(error: unknown): AppError {
  if (error instanceof AppError) return error;
  if (error && typeof error === 'object') {
    const e = error as { code?: unknown; message?: unknown; details?: unknown; status?: unknown; name?: unknown };
    const message = typeof e.message === 'string' ? e.message : '';
    const pgCode = typeof e.code === 'string' ? e.code : '';
    const status = /^PT\d{3}$/.test(pgCode) ? Number(pgCode.slice(2)) : typeof e.status === 'number' ? e.status : 400;
    if (isAppErrorCode(message)) return new AppError(message, status, typeof e.details === 'string' ? e.details : undefined);
    if (pgCode === '42501' || pgCode === 'PGRST301' || status === 401) return new AppError('NOT_AUTHENTICATED', 401);
    if (e.name === 'TypeError' || /fetch|network|Failed to fetch|Load failed/i.test(message)) return new AppError('NETWORK_ERROR', 0);
    if (status >= 500) return new AppError('BACKEND_UNAVAILABLE', status);
  }
  return new AppError('UNKNOWN', 500);
}
